import type { CustomerContact } from "../types";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  MoreVertical,
  Star,
  Edit,
  Trash2,
  CheckCircle,
  MapPin,
  Building2,
  Phone,
  Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ContactCardProps {
  contact: CustomerContact;
  onEdit: (contact: CustomerContact) => void;
  onDelete: (contact: CustomerContact) => void;
  onToggleFavorite: (contact: CustomerContact) => void;
  onSetDefault: (contact: CustomerContact) => void;
}

export function ContactCard({
  contact,
  onEdit,
  onDelete,
  onToggleFavorite,
  onSetDefault,
}: ContactCardProps) {
  const formatAddress = () => {
    const parts = [contact.addressLine1];
    if (contact.addressLine2) parts.push(contact.addressLine2);
    parts.push(`${contact.addressCity}, ${contact.addressProvince}`);
    if (contact.addressPostalCode) parts.push(contact.addressPostalCode);
    parts.push(contact.addressCountry);
    return parts.join(", ");
  };

  return (
    <Card
      className={cn(
        "hover:shadow-md transition-shadow",
        contact.isDefault && "ring-2 ring-primary"
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            {/* Header */}
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg">{contact.contactName}</h3>
              {contact.isDefault && (
                <Badge variant="default" className="text-xs">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Default
                </Badge>
              )}
              {contact.label && (
                <Badge variant="outline" className="text-xs">
                  {contact.label}
                </Badge>
              )}
            </div>

            {/* Company */}
            {contact.contactCompany && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="w-4 h-4" />
                <span>{contact.contactCompany}</span>
              </div>
            )}

            {/* Address */}
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{formatAddress()}</span>
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {contact.contactPhone && (
                <div className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  <span>{contact.contactPhone}</span>
                </div>
              )}
              {contact.contactEmail && (
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  <span className="truncate max-w-[200px]">
                    {contact.contactEmail}
                  </span>
                </div>
              )}
            </div>

            {contact.pickupInstructions && (
              <p className="text-sm text-muted-foreground line-clamp-2 italic">
                Pickup instructions: {contact.pickupInstructions}
              </p>
            )}
            {contact.deliveryInstructions && (
              <p className="text-sm text-muted-foreground line-clamp-2 italic">
                Default delivery instructions: {contact.deliveryInstructions}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleFavorite(contact)}
              className={cn(
                contact.isFavorite && "text-yellow-500 hover:text-yellow-600"
              )}
            >
              <Star
                className={cn("w-4 h-4", contact.isFavorite && "fill-current")}
              />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(contact)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                {!contact.isDefault && (
                  <DropdownMenuItem onClick={() => onSetDefault(contact)}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Set as Default
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => onDelete(contact)}
                  className="text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
